<template>
  <div>
    <v-row
      align="center"
      dense
      class="my-5"
    >
      <v-col cols="auto">
        <v-text-field
          v-model="filters.search"
          :placeholder="$t('text_input.search_for_event.title')"
          solo
          hide-details
          style="width: 400px"
          append-icon="far fa-search"
          @input="search()"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-for="eventType in mainEventTypesList"
          :key="'eventTypeButton'+eventType.key"
          :text="filters.eventType !== eventType.key"
          :dark="filters.eventType === eventType.key"
          class="text-none"
          :color="filters.eventType === eventType.key ? eventType.color : undefined"
          @click="filters.eventType === eventType.key ? filters.eventType = undefined : filters.eventType = eventType.key"
        >
          {{ eventType.text }}
        </v-btn>
        <v-menu bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              class="text-none"
              :color="currentSelectedEventType && currentSelectedEventType.is_other ? currentSelectedEventType.color : undefined"
              :text="!currentSelectedEventType || !currentSelectedEventType.is_other"
              :dark="currentSelectedEventType && currentSelectedEventType.is_other"
              v-on="on"
            >
              {{ currentSelectedEventType && currentSelectedEventType.is_other ? currentSelectedEventType.text : $t('not_attended_reason.other') }}
              <v-icon
                right
                x-small
              >
                far fa-chevron-down
              </v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list dense>
              <v-list-item
                v-if="currentSelectedEventType && currentSelectedEventType.is_other"
                @click="filters.eventType = undefined"
              >
                <v-list-item-title>
                  {{ $t('remove_filter.title') }}
                </v-list-item-title>
                <v-list-item-action>
                  <v-icon small>
                    far fa-times
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="currentSelectedEventType && currentSelectedEventType.is_other" />
              <v-list-item
                v-for="eventType in otherEventTypesList"
                :key="'otherEventType'+eventType.key"
                @click="filters.eventType = eventType.key"
              >
                <v-list-item-title>
                  {{ eventType.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-select
          v-model="filters.tags"
          solo
          multiple
          hide-details
          :placeholder="$t('dropdown.search_by_tags.title')"
          chips
          small-chips
          deletable-chips
          :items="tags.data"
          :loading="tags.loading"
          item-value="id"
          item-text="label"
          style="width: 300px"
          clearable
          @change="fetchMedia()"
        >
          <template v-slot:selection="{ item, index }">
            <span
              v-if="index === 0"
              class="text-grey text-body-2 align-self-center"
            >
              {{ $tc("dropdown.tags_selected", filters.tags.length , {num: filters.tags.length }) }}
            </span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="auto">
        <v-menu
          v-model="calendarFilterActive"
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="dateRangeText"
              :label="$t('filter_by_date.label')"
              solo
              hide-details
              style="width: 290px"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.dateRange"
            :first-day-of-week="1"
            no-title
            range
            :min="filters.dateRange.length === 1 ? filters.dateRange[0] : undefined"
            @change="calendarFilterActive = false; fetchMedia()"
          />
        </v-menu>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="$can('create-player-media')"
          color="grey darken-2"
          text
          class="text-none"
          @click="$refs.uploadFile.click()"
        >
          {{ $t('button.browse') }}
          <v-icon
            right
            small
          >
            fal fa-upload
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <input
      id="upload-file"
      ref="uploadFile"
      type="file"
      multiple
      style="display: none"
      accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf,application/octet-stream,.dkg,.csv"
      @input="handleFileChange"
    >

    <v-divider class="mt-5" />
    <div
      style="height: 1px"
      class="mb-5"
    >
      <v-progress-linear
        v-if="media.loading"
        indeterminate
        height="1px"
      />
    </div>

    <div>
      <v-row>
        <v-col
          v-for="mediaItem in media.data"
          :key="mediaItem.id"
          cols="auto"
        >
          <media-grid-item
            :media-item="mediaItem"
            style="width: 260px"
            show-event-details
            :can-delete="$can('delete-player-media')"
            @preview="preview"
            @delete="mediaItem.id ? startDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
          />
        </v-col>
      </v-row>
    </div>
    <div class="mb-10">
      <div
        v-if="media.pagination && media.pagination.last_page > 1"
        class="d-flex justify-center align-center py-5"
      >
        <pagination
          v-model="media.pagination.current_page"
          :last-page="media.pagination.last_page"
          @input="fetchMedia()"
        />
      </div>
    </div>

    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="media.data"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
      :view-only="!$can('edit-player-media')"
      :can-delete="$can('delete-player-media')"
      @media:updated="updateMedia"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="destroy()"
    />
  </div>
</template>

<script>
import { PlayerApiService } from '@/data/services'
import Pagination from '@/components/Pagination.vue'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import { EventTypes } from '@/data/models'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import { MediaApiService } from '@/data/services/MediaApiService'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import debounce from 'lodash/debounce'
import { TagApiService } from '@/data/services/TagApiService'
import _axios from '@/plugins/axios'

export default {
  name: 'PlayerMediaView',
  components: {
    MediaViewerDialog,
    DeleteConfirmationDialog,
    MediaGridItem,
    Pagination
  },
  mixins: [deleteConfirmationMixin],
  props: {
    player: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      media: {
        loading: false,
        data: [],
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 30
        }
      },
      filters: {
        eventType: undefined,
        search: '',
        dateRange: [this.$dayjs().subtract(12, 'month').format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')],
        tags: []
      },
      mediaViewerDialog: {
        visible: false
      },
      calendarFilterActive: false,
      deleteMedia: undefined,
      deleting: false,
      tags: {
        loading: false,
        data: []
      }
    }
  },
  computed: {
    dateRangeText() {
      const firstDate = this.filters.dateRange[0] ? this.$dayjs(this.filters.dateRange[0]).format('ll') : ''
      const secondDate = this.filters.dateRange[1] ? this.$dayjs(this.filters.dateRange[1]).format('ll') : ''

      return firstDate + ' - ' + secondDate
    },
    currentSelectedEventType() {
      if (!this.filters.eventType) return undefined

      const eventTypes = Object.keys(EventTypes).map(k => {
        return {
          ...EventTypes[k],
          key: k
        }
      })

      return eventTypes.find(t => t.key === this.filters.eventType)
    }
  },
  watch: {
    'filters.eventType': function () {
      this.fetchMedia()
    }
  },
  created() {
    this.fetchTags()

    this.eventTypes = EventTypes

    this.mainEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === false)
    this.otherEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === true)

    this.fetchMedia()
  },
  methods: {
    async fetchMedia() {
      try {
        this.media.loading = true

        const queryParams = {
          page: this.media.pagination.current_page,
          per_page: this.media.pagination.per_page,
          event_type: this.filters.eventType,
          from: this.filters.dateRange[0] ? this.filters.dateRange[0] : undefined,
          to: this.filters.dateRange[1] ? this.filters.dateRange[1] : undefined,
          search: this.filters.search || undefined,
          tags: this.filters.tags
        }

        const resp = await PlayerApiService.getMedia(this.player.id, queryParams)

        this.media.data = resp.data
        this.media.pagination = resp.meta
      } catch (e) {
        this.media.data = []
        this.media.pagination.current_page = 1
        this.media.pagination.last_page = 1
      } finally {
        this.media.loading = false
      }
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'csv']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.data.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.data.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.processUpload()
    },
    async processUpload() {
      let formData

      for (const file of this.media.data.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post(`players/${this.player.id}/media-upload`, formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media.data)

          this.fetchMedia()
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })

          file.error = this.$t('alert.uploading_file.error')
        })
      }
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    startDelete(media) {
      this.deleteMedia = media
      this.deleteConfirmationDialogVisible = true
    },
    async destroy() {
      if (!this.deleteMedia) return

      const id = this.deleteMedia.id

      try {
        this.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.data.findIndex(m => m.id === id)

        if (index > -1) this.media.data.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        this.deleteMedia = undefined
      } catch (e) {

      } finally {
        this.deleting = false
      }
    },
    updateMedia(media) {
      const index = this.media.data.findIndex(m => m.id === media.id)

      if (index === -1) return

      this.media.data.splice(index, 1, media)
    },
    search: debounce(function () {
      this.fetchMedia()
    }, 500),
    async fetchTags() {
      this.tags.loading = true

      try {
        const result = await TagApiService.getAll()
        this.tags.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_tags.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.tags.loading = false
      }
    }
  }
}
</script>

<style scoped>

</style>
